import { useEffect, useState } from "react";
import { listJobPostings, saveJobPosting, deleteJobPosting, submitInternalApplication, listInternalApplications, decideInternalApplication, fetchRoles } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Briefcase, Plus, Send, Loader2, Users, CheckCircle2, XCircle, Star, Trash2 } from "lucide-react";

export function JobBoard({ employeeId }) {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyOn, setApplyOn] = useState(null);
  const [cover, setCover] = useState("");
  const [highlights, setHighlights] = useState("");
  const [myApps, setMyApps] = useState([]);

  const reload = () => {
    setLoading(true);
    listJobPostings("open").then((d) => setPostings(d.postings || [])).finally(() => setLoading(false));
    if (employeeId) listInternalApplications({ employee_id: employeeId }).then((d) => setMyApps(d.applications || []));
  };
  useEffect(reload, [employeeId]);

  const submit = async () => {
    if (!cover.trim()) { toast.error("Niyet mektubu boş olamaz"); return; }
    try {
      await submitInternalApplication({
        posting_id: applyOn.id,
        employee_id: employeeId,
        cover_letter: cover,
        highlights: highlights.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Başvurun iletildi");
      setApplyOn(null); setCover(""); setHighlights("");
      reload();
    } catch {
      toast.error("Başvuru başarısız");
    }
  };

  return (
    <div className="space-y-4" data-testid="job-board">
      <div className="card-surface p-4">
        <div className="label-eyebrow flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-indigo-600" /> İç Fırsatlar (YTN-28)</div>
        <div className="text-sm text-slate-600 mt-1">İlana niyet mektubunla başvur, yönetici + HR kararı sana bildirilir.</div>
      </div>
      {loading ? (
        <div className="card-surface p-6 flex items-center justify-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>
      ) : postings.length === 0 ? (
        <div className="card-surface p-8 text-center text-slate-500">Şu an açık iç ilan yok.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {postings.map((p) => {
            const already = myApps.find((a) => a.posting_id === p.id);
            return (
              <div key={p.id} className="card-surface p-4 hover:shadow-md transition-all" data-testid={`job-posting-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.department} · {p.career_band ? `Band ${p.career_band}` : ""}{p.location ? ` · ${p.location}` : ""}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{p.application_count || 0} başvuru</span>
                </div>
                <div className="text-xs text-slate-600 mt-2 line-clamp-3">{p.description}</div>
                {p.requirements?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.requirements.slice(0, 4).map((r, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-100">{r}</span>)}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] text-slate-500">{p.deadline ? `Son tarih: ${p.deadline}` : ""}</div>
                  {already ? (
                    <span className={`text-[10px] px-2 py-1 rounded font-medium ${already.status === "approved" ? "bg-emerald-50 text-emerald-700" : already.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                      Başvurun: {already.status === "pending" ? "Bekliyor" : already.status === "approved" ? "Onaylandı" : already.status === "shortlisted" ? "Kısa liste" : "Reddedildi"}
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => setApplyOn(p)} data-testid={`apply-btn-${p.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Başvur</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Application dialog */}
      <Dialog open={!!applyOn} onOpenChange={(v) => !v && setApplyOn(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="apply-dialog">
          <DialogHeader><DialogTitle>Niyet Mektubu — {applyOn?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder="Neden bu rol? Ne katkı sağlayacaksın? Şimdiye kadarki hangi deneyimlerin bu rolle örtüşüyor?" rows={6} value={cover} onChange={(e) => setCover(e.target.value)} data-testid="apply-cover-letter" />
            <Textarea placeholder="Öne çıkanlar (her satır bir madde) — opsiyonel" rows={3} value={highlights} onChange={(e) => setHighlights(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOn(null)}>İptal</Button>
            <Button onClick={submit} data-testid="apply-submit">Başvuruyu Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export function JobPostingsAdmin() {
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ role_id: "", description: "", location: "", deadline: "", requirements: "" });
  const [viewApplications, setViewApplications] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState({});

  const reload = () => {
    listJobPostings("open").then((d) => setPostings(d.postings || []));
    listInternalApplications({}).then((d) => setApplications(d.applications || []));
  };
  useEffect(() => {
    reload();
    fetchRoles().then((r) => setRoles(Array.isArray(r) ? r : (r?.roles || []))).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.role_id || !form.description.trim()) { toast.error("Rol ve açıklama zorunlu"); return; }
    try {
      await saveJobPosting({
        role_id: form.role_id,
        description: form.description,
        location: form.location,
        deadline: form.deadline || null,
        requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        status: "open",
      });
      toast.success("İlan oluşturuldu");
      setForm({ role_id: "", description: "", location: "", deadline: "", requirements: "" });
      setDlgOpen(false);
      reload();
    } catch { toast.error("İlan oluşturulamadı"); }
  };

  const decide = async (appId, status) => {
    try {
      await decideInternalApplication(appId, { status, decision_notes: decisionNotes[appId] || "" });
      toast.success(`Başvuru ${status === "approved" ? "onaylandı" : status === "rejected" ? "reddedildi" : "kısa listeye alındı"}`);
      reload();
    } catch { toast.error("Karar kaydedilemedi"); }
  };

  const removePosting = async (id) => {
    if (!window.confirm("İlan silinsin mi?")) return;
    await deleteJobPosting(id);
    reload();
  };

  const filteredApps = viewApplications ? applications.filter((a) => a.posting_id === viewApplications.id) : [];

  return (
    <div className="space-y-4" data-testid="job-postings-admin">
      <div className="card-surface p-4 flex items-center justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-indigo-600" /> İç İlan Yönetimi (YTN-28, 29)</div>
          <div className="text-sm text-slate-600 mt-1">Aç, düzenle, kapat. Başvuruları önceliklendir.</div>
        </div>
        <Button size="sm" onClick={() => setDlgOpen(true)} data-testid="new-posting-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Yeni İlan</Button>
      </div>

      {postings.length === 0 ? (
        <div className="card-surface p-6 text-center text-slate-500">Açık ilan yok.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {postings.map((p) => (
            <div key={p.id} className="card-surface p-4" data-testid={`admin-posting-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.department} · {p.career_band || ""}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setViewApplications(p)}><Users className="w-3.5 h-3.5 mr-1" /> {p.application_count || 0}</Button>
                  <Button size="icon" variant="ghost" onClick={() => removePosting(p.id)}><Trash2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                </div>
              </div>
              <div className="text-xs text-slate-600 mt-2 line-clamp-2">{p.description}</div>
            </div>
          ))}
        </div>
      )}

      {/* New posting dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Yeni İç İlan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
              <SelectTrigger className="bg-white"><SelectValue placeholder="Rol seç..." /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.title} ({r.career_band})</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="İlan açıklaması, sorumluluklar..." />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Lokasyon" />
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <Textarea rows={3} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="Gereksinimler (her satır bir madde)" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>İptal</Button>
            <Button onClick={submit}>Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applications dialog */}
      <Dialog open={!!viewApplications} onOpenChange={(v) => !v && setViewApplications(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Başvurular — {viewApplications?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {filteredApps.length === 0 ? <div className="text-sm text-slate-500 italic">Henüz başvuru yok.</div> : filteredApps.map((a) => (
              <div key={a.id} className="p-3 rounded-lg border border-slate-200" data-testid={`app-${a.id}`}>
                <div className="flex items-start gap-3">
                  <img src={a.employee_avatar} alt={a.employee_name} className="w-10 h-10 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{a.employee_name}</div>
                    <div className="text-xs text-slate-500">{a.employee_title}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <span className="text-indigo-600 font-medium">Uygunluk: %{a.readiness_pct}</span>
                      <span className="text-slate-500">({a.criteria_met}/{a.criteria_total} kriter)</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${a.status === "approved" ? "bg-emerald-50 text-emerald-700" : a.status === "rejected" ? "bg-red-50 text-red-700" : a.status === "shortlisted" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{a.status}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-700 italic bg-slate-50 p-2 rounded">{a.cover_letter}</div>
                    {a.highlights?.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-[11px] text-slate-600">{a.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                    )}
                    {a.status === "pending" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Input placeholder="Karar notu (opsiyonel)" className="flex-1 h-8" value={decisionNotes[a.id] || ""} onChange={(e) => setDecisionNotes({ ...decisionNotes, [a.id]: e.target.value })} />
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => decide(a.id, "shortlisted")} data-testid={`shortlist-${a.id}`}><Star className="w-3 h-3 mr-1" /> Kısa Liste</Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Onayla</Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide(a.id, "rejected")} data-testid={`reject-${a.id}`}><XCircle className="w-3 h-3 mr-1" /> Reddet</Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
